"use client";

import * as React from "react";
import { ChevronDown, ChevronRight, Copy, Eye, EyeOff, WrapText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";
import { isPlainRecord } from "@/utils/json-parser";
import { maskSensitiveData } from "@/utils/sensitive-data-masker";
import { JSON_VIEWER_MAX_DEPTH, JSON_VIEWER_MAX_NODES } from "@/lib/constants";
import { toast } from "sonner";

interface RenderBudget {
  count: number;
}

interface JsonViewerControls {
  expandToken: number;
  collapseToken: number;
  searchTerm: string;
  wrapLines: boolean;
}

const JsonViewerContext = React.createContext<JsonViewerControls>({
  expandToken: 0,
  collapseToken: 0,
  searchTerm: "",
  wrapLines: false,
});

function highlightText(text: string, search: string): React.ReactNode {
  if (!search) return text;
  const lower = text.toLowerCase();
  const needle = search.toLowerCase();
  if (!lower.includes(needle)) return text;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let index = lower.indexOf(needle);
  let key = 0;
  while (index !== -1) {
    if (index > cursor) parts.push(text.slice(cursor, index));
    parts.push(
      <mark key={key++} className="rounded bg-warning/40 text-inherit">
        {text.slice(index, index + needle.length)}
      </mark>,
    );
    cursor = index + needle.length;
    index = lower.indexOf(needle, cursor);
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

function valueTypeClass(value: unknown): string {
  if (value === null) return "text-muted-foreground";
  switch (typeof value) {
    case "string":
      return "text-success";
    case "number":
      return "text-chart-2";
    case "boolean":
      return "text-chart-4";
    default:
      return "text-foreground";
  }
}

function formatPrimitive(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return `"${value}"`;
  return String(value);
}

function countSearchMatches(value: unknown, search: string): number {
  if (!search) return 0;
  const needle = search.toLowerCase();
  let count = 0;

  const visit = (node: unknown, depth: number): void => {
    if (depth > JSON_VIEWER_MAX_DEPTH) return;
    if (isPlainRecord(node)) {
      for (const [key, val] of Object.entries(node)) {
        if (key.toLowerCase().includes(needle)) count += 1;
        visit(val, depth + 1);
      }
    } else if (Array.isArray(node)) {
      node.forEach((item) => visit(item, depth + 1));
    } else if (node !== null && node !== undefined) {
      if (String(node).toLowerCase().includes(needle)) count += 1;
    }
  };

  visit(value, 0);
  return count;
}

interface JsonNodeProps {
  label: string | number | null;
  value: unknown;
  depth: number;
  budget: RenderBudget;
  isLast: boolean;
}

function JsonNode({ label, value, depth, budget }: JsonNodeProps) {
  const controls = React.useContext(JsonViewerContext);
  const isContainer = isPlainRecord(value) || Array.isArray(value);
  const [expanded, setExpanded] = React.useState(depth < 2);
  const lastExpandToken = React.useRef(controls.expandToken);
  const lastCollapseToken = React.useRef(controls.collapseToken);

  React.useEffect(() => {
    if (controls.expandToken !== lastExpandToken.current) {
      lastExpandToken.current = controls.expandToken;
      if (isContainer) setExpanded(true);
    }
  }, [controls.expandToken, isContainer]);

  React.useEffect(() => {
    if (controls.collapseToken !== lastCollapseToken.current) {
      lastCollapseToken.current = controls.collapseToken;
      if (isContainer) setExpanded(false);
    }
  }, [controls.collapseToken, isContainer]);

  budget.count += 1;
  if (budget.count > JSON_VIEWER_MAX_NODES) {
    return <div className="text-xs text-muted-foreground">… output truncated (too many values to render)</div>;
  }

  if (depth > JSON_VIEWER_MAX_DEPTH) {
    return (
      <div className="pl-4 text-xs text-muted-foreground">
        {label !== null ? <span className="mr-1">{label}:</span> : null}
        [max depth reached]
      </div>
    );
  }

  if (!isContainer) {
    return (
      <div className={cn("flex items-start gap-1 py-0.5 pl-5 text-xs", controls.wrapLines ? "whitespace-pre-wrap break-all" : "whitespace-nowrap")}>
        {label !== null ? (
          <span className="shrink-0 text-muted-foreground">{highlightText(String(label), controls.searchTerm)}:</span>
        ) : null}
        <span className={valueTypeClass(value)}>{highlightText(formatPrimitive(value), controls.searchTerm)}</span>
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((v, i) => [i, v] as const)
    : Object.entries(value as Record<string, unknown>);
  const isArray = Array.isArray(value);
  const bracketOpen = isArray ? "[" : "{";
  const bracketClose = isArray ? "]" : "}";

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-1 rounded py-0.5 text-left hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
        {label !== null ? (
          <span className="text-muted-foreground">{highlightText(String(label), controls.searchTerm)}:</span>
        ) : null}
        <span className="text-muted-foreground">
          {bracketOpen}
          {!expanded ? `${entries.length} ${isArray ? "items" : "keys"}${bracketClose}` : ""}
        </span>
      </button>
      {expanded ? (
        <div className="ml-3 border-l pl-1">
          {entries.length === 0 ? (
            <div className="pl-5 text-muted-foreground">empty {isArray ? "array" : "object"}</div>
          ) : (
            entries.map(([key, val], index) => (
              <JsonNode
                key={key}
                label={key}
                value={val}
                depth={depth + 1}
                budget={budget}
                isLast={index === entries.length - 1}
              />
            ))
          )}
          <div className="pl-5 text-muted-foreground">{bracketClose}</div>
        </div>
      ) : null}
    </div>
  );
}

interface JsonViewerProps {
  data: unknown;
  emptyMessage?: string;
}

export function JsonViewer({ data, emptyMessage = "No data" }: JsonViewerProps) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [expandToken, setExpandToken] = React.useState(0);
  const [collapseToken, setCollapseToken] = React.useState(0);
  const [wrapLines, setWrapLines] = React.useState(false);
  const [revealed, setRevealed] = React.useState(false);

  const displayData = React.useMemo(() => (revealed ? data : maskSensitiveData(data)), [data, revealed]);
  const matchCount = React.useMemo(() => countSearchMatches(displayData, searchTerm), [displayData, searchTerm]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- budget is intentionally reset whenever displayData changes
  const budget = React.useMemo<RenderBudget>(() => ({ count: 0 }), [displayData]);

  const isEmpty = data === null || data === undefined || (isPlainRecord(data) && Object.keys(data).length === 0);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(displayData, null, 2));
      toast.success("JSON copied to clipboard");
    } catch {
      toast.error("Could not copy JSON");
    }
  };

  return (
    <div className="rounded-md border">
      <div className="flex flex-wrap items-center gap-1.5 border-b bg-muted/40 p-1.5">
        <div className="relative flex-1 min-w-32">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search JSON…"
            className="h-7 text-xs"
            aria-label="Search within JSON"
          />
        </div>
        {searchTerm ? (
          <span className="text-xs text-muted-foreground shrink-0">
            {matchCount} match{matchCount === 1 ? "" : "es"}
          </span>
        ) : null}
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setExpandToken((t) => t + 1)}>
          Expand all
        </Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCollapseToken((t) => t + 1)}>
          Collapse all
        </Button>
        <Toggle pressed={wrapLines} onPressedChange={setWrapLines} size="sm" className="h-7" aria-label="Toggle line wrapping">
          <WrapText className="size-3.5" />
        </Toggle>
        <Toggle pressed={revealed} onPressedChange={setRevealed} size="sm" className="h-7" aria-label="Reveal sensitive values">
          {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          <span className="ml-1 text-xs">{revealed ? "Hide sensitive" : "Reveal sensitive"}</span>
        </Toggle>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={handleCopy}>
          <Copy className="size-3.5" />
        </Button>
      </div>
      <div className="max-h-80 overflow-auto p-2">
        {isEmpty ? (
          <p className="p-2 text-xs text-muted-foreground">{emptyMessage}</p>
        ) : (
          <JsonViewerContext.Provider value={{ expandToken, collapseToken, searchTerm, wrapLines }}>
            <JsonNode label={null} value={displayData} depth={0} budget={budget} isLast />
          </JsonViewerContext.Provider>
        )}
      </div>
    </div>
  );
}
